import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import { Theme, ColorBadge, Button, AlertService, TextBox, TrashIcon } from '@spoiler-alert/ui-library';
import { type match as matchType } from 'react-router';
import moment from 'moment';
import accounting from 'accounting';
import {
  AdminNumocoSingleOrder,
  AdminNumocoSingleOrderType,
  InvoiceStatus,
  NumocoOrderItem,
  POStatus,
} from './numoco-queries';
import { VColumn, VTable } from '../../components/v-table';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import DownloadButton from './download-button';
import { fetchWithToken } from '../../services/fetch-with-token';
import appSettings from '../../app-settings';
import NewButton from '../import-manager/new-button';
import { VTableType } from 'src/components/v-table/v-table-types';

const useStyles = createUseStyles({
  wrap: {
    '& h2, & h3, & h4': {
      margin: [0, 0, 5],
      fontWeight: 300,
    },
  },
  tableWrap: {
    position: 'relative',
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  trashButton: {
    margin: [-7, 0, -10, 0],
  },
  panelWrap: {
    display: 'flex',
    gap: 15,
    marginBottom: 15,
  },
  downloads: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    margin: 0,
    width: 300,
    alignItems: 'start',
    background: Theme.grey5,
    border: `1px solid ${Theme.grey30}`,
    padding: 10,
  },
  panelRight: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  header: {
    padding: [10, 0],
    display: 'flex',
    gap: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    borderBottom: `1px solid ${Theme.grey10}`,
    marginBottom: 15,
    justifyContent: 'space-between',
  },
  mainInfo: {
    display: 'flex',
    gap: 15,
    padding: 0,
    '&>div': {
      width: '50%',
      border: `1px solid ${Theme.tableBorderColor}`,
      padding: [10, 15, 15, 15],
    },
  },
  address: {
    padding: 10,
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  poWrap: {
    padding: [10, 15, 15, 15],
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  poItems: {
    display: 'flex',
    gap: 15,
    flexWrap: 'wrap',
  },
  poItem: {
    flexGrow: 1,
    border: `1px solid ${Theme.tableBorderColor}`,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
});

export type BadgeTheme = 'grey' | 'green' | 'yellow' | 'orange' | 'teal' | 'red';

const InvoiceStatusTheme: Record<InvoiceStatus, BadgeTheme> = {
  [InvoiceStatus.OPEN]: 'teal',
  [InvoiceStatus.VOIDED]: 'red',
  [InvoiceStatus.PAID]: 'green',
  [InvoiceStatus.PENDING]: 'yellow',
  [InvoiceStatus.DRAFT]: 'grey',
};

const POStatusTheme: Record<POStatus, BadgeTheme> = {
  [POStatus.PENDING]: 'yellow',
  [POStatus.ACCEPTED]: 'green',
  [POStatus.READY_TO_SEND]: 'grey',
};

interface NumocoSingleOrderParams {
  match: matchType<{ orderId: string }>;
}

const NumocoSingleOrder: React.FC<NumocoSingleOrderParams> = ({ match }) => {
  const classes = useStyles();
  const [generating, setGenerating] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);

  const { orderId } = match.params;

  const { data, refetch } = useQuery<AdminNumocoSingleOrderType>(AdminNumocoSingleOrder, {
    variables: {
      orderId,
    },
  });

  const order = data?.adminNumocoSingleOrder || null;

  useEffect(() => {
    TitleService.setTitles('NUMOCO Order');
    let title = 'Order Detail';
    if (order) {
      title = order.name;
    }
    Breadcrumbs.set([
      {
        url: '/numoco-orders',
        title: 'NUMOCO Orders',
      },
      {
        url: `/numoco-orders/order/${orderId}`,
        title,
      },
    ]);
    setEmailList(order?.packingListContacts || []);
  }, [orderId, order]);

  const itemColumns = useMemo(() => {
    return [
      new VColumn({
        field: '_id',
        displayName: 'ID',
        sortable: true,
      }),
      new VColumn({
        field: 'description',
        displayName: 'Description',
        sortable: true,
      }),
      new VColumn({
        field: 'itemName',
        displayName: 'Item Name',
        sortable: true,
      }),
      new VColumn({
        field: 'reference_id',
        displayName: 'External Transaction ID',
        sortable: true,
      }),
      new VColumn({
        field: 'unit_cost',
        displayName: 'Unit Cost',
        sortable: true,
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'quantity',
        displayName: 'Quantity',
        sortable: true,
        formatter: (value) => accounting.formatNumber(value as number),
      }),
      new VColumn({
        field: 'line_total',
        displayName: 'Line Total',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'desc',
        formatter: (value) => accounting.formatMoney(value),
      }),
    ];
  }, []);

  const preGeneratedReconciliationForm = useMemo(() => {
    return order?.generated_sheets.find((file) => file.type === 'Reconciliation Sheet Form');
  }, [order?.generated_sheets]);

  const awardSheet = useMemo(() => {
    return order?.generated_sheets.find((file) => file.type === 'Award Sheet');
  }, [order?.generated_sheets]);

  const changeEmailValue = (value: string, index: number) => {
    const newEmailList = Array.from(emailList);
    newEmailList[index] = value;
    setEmailList(newEmailList);
  };

  const addNewEmail = () => {
    const newEmailList = Array.from(emailList);
    newEmailList.push('');
    setEmailList(newEmailList);
  };

  const deleteEmail = (index: number) => {
    const newEmailList = Array.from(emailList);
    newEmailList.splice(index, 1);
    setEmailList(newEmailList);
  };

  const handleSendPackingList = async () => {
    if (!order) return;
    setGenerating(true);
    try {
      const result = await fetchWithToken(`${appSettings.GRAPHQL_SERVER_URL}numoco/reconciliation-form`, {
        body: JSON.stringify({
          awardSheetFilePath: awardSheet?.path,
          sellerSiteId: order.seller_site_id,
          buyerSiteId: order.buyer_site_id,
          transactionId: order.items[0].reference_id,
          packingListUrl: order.packing_list_pdf_url,
          numocoOrderNumber: order.order_number,
          emailList: emailList,
          existingSheet: preGeneratedReconciliationForm?.path,
          referenceIds: order.items.map((i) => i.reference_id),
          packingListFileName: `${order.name}-Packing_List.pdf`,
        }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (result.ok) {
        const body = await result.json();
        if (!(body?.errors.length === 0)) throw new Error(body.results.errors[0]);
        AlertService.alert({
          type: 'info',
          message: <span>Email was successfully sent to {body.results.contacts.join(', ')}</span>,
          autoDismiss: true,
          dismissDelay: 5000,
        });
        refetch();
      }
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: <span>{error}</span>,
        autoDismiss: false,
        dismissDelay: 5000,
      });
    }
    setGenerating(false);
  };

  const AwaitingPOTable = VTable as VTableType<NumocoOrderItem>;

  return (
    <div className={classes.wrap}>
      {order && (
        <>
          <div className={classes.header}>
            <h2>Order ID: {order._id}</h2>
            <h3>Order #{order.order_number}</h3>
            <h3>Total: {accounting.formatMoney(order.total_amount)}</h3>
            <ColorBadge id={`${order._id}-status`} theme="grey">
              {order.status}
            </ColorBadge>
          </div>
          <div className={classes.panelWrap}>
            <div className={classes.downloads}>
              <h4>Downloads</h4>
              <div>
                {order.packing_list_pdf_url && (
                  <DownloadButton
                    kind="Packing List"
                    fileName={`${order.name}-Packing_List.pdf`}
                    downloadPath={order.packing_list_pdf_url}
                  />
                )}
              </div>
              {order.generated_sheets.map(({ type, path }) => (
                <div key={path}>
                  <DownloadButton
                    kind={type}
                    path={path}
                    downloadPath={`${appSettings.GRAPHQL_SERVER_URL}download/?path=${encodeURIComponent(path)}&source=FTP`}
                  />
                </div>
              ))}
              <h4>Emails</h4>
              <div style={{ width: '100%' }}>
                {emailList.map((p, index) => {
                  return (
                    <div style={{ display: 'flex', margin: '5px' }}>
                      <TextBox
                        key={`textbox-${index}`}
                        style={{ width: 100, margin: 10 }}
                        value={p}
                        onChange={(value: string) => changeEmailValue(value, index)}
                        small
                      />
                      <Button
                        key={`btn-${index}`}
                        warning
                        resting={true}
                        icon={
                          TrashIcon as React.FC<{
                            style?: React.CSSProperties;
                            className?: string;
                          }>
                        }
                        className={classes.trashButton}
                        onClick={(e: any) => {
                          e.preventDefault();
                          deleteEmail(index);
                        }}
                      />
                    </div>
                  );
                })}
                <div>
                  <NewButton onClick={addNewEmail}>New</NewButton>
                </div>
              </div>
            </div>
            <div className={classes.panelRight}>
              <div>
                {awardSheet ? (
                  <Button onClick={handleSendPackingList} loading={generating} loadingText="Sending...">
                    {preGeneratedReconciliationForm ? 'Resend' : 'Send'} Packing List & Reconciliation Form
                  </Button>
                ) : (
                  <h4>We couldn&lsquo;t find an Award Sheet! You might be missing transaction IDs.</h4>
                )}
              </div>
              <div className={classes.mainInfo}>
                <div>
                  <h4>Seller: {order.supplier_company_name}</h4>
                  {order.pickup_address && (
                    <div className={classes.address}>
                      <h4>Origin address:</h4>
                      {order.pickup_address.split('\n').map((p) => (
                        <div key={p}>{p}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4>Buyer: {order.buyer_company_name}</h4>
                  {order.delivery_address && (
                    <div className={classes.address}>
                      <h4>Destination address:</h4>
                      {order.delivery_address.split('\n').map((p) => (
                        <div key={p}>{p}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.poWrap}>
                <h3>Purchase Orders</h3>
                <div className={classes.poItems}>
                  {order.purchase_orders.map((po) => (
                    <div key={po.id} className={classes.poItem}>
                      <div>NUMOCO ID: {po.id}</div>
                      <div>
                        <ColorBadge id={po.id} theme={POStatusTheme[po.status]}>
                          {po.status}
                        </ColorBadge>
                      </div>
                      <div>PO Number: {po.po_number}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={classes.poWrap}>
                <h3>Invoices</h3>
                {order.invoices.length === 0 && <div>No Invoices</div>}
                <div className={classes.poItems}>
                  {order.invoices.map((inv) => (
                    <div key={inv.id} className={classes.poItem}>
                      <div>NUMOCO ID: {inv.id}</div>
                      <div>Invoice #: {inv.invoice_number}</div>
                      <div>
                        <ColorBadge id={inv.id} theme={InvoiceStatusTheme[inv.status]}>
                          {inv.status}
                        </ColorBadge>
                      </div>
                      <div>Total Amount: {accounting.formatMoney(inv.total_amount)}</div>
                      <div>Was Sent? {inv.was_sent ? 'Yes' : 'No'}</div>
                      <div>Due Date: {moment(inv.due_date).format('MMM d, YYYY')}</div>
                      <div>Close Time: {moment(inv.close_time).format('MMM d, YYYY')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={classes.tableWrap}>
            <AwaitingPOTable cols={itemColumns} data={order.items} />
          </div>
        </>
      )}
    </div>
  );
};

export default NumocoSingleOrder;
